// Consulta do cliente no ERP (Microuniverso) — SÓ LEITURA.
//
// Fluxo da tela, na ordem em que a crediarista trabalha:
//   1. Abriu pela conversa -> tenta adivinhar o cliente pelo telefone.
//   2. O palpite NUNCA é aplicado sozinho: ela confere nome/CPF e confirma.
//      (Telefone fixo é compartilhado entre cadastros: num teste real um número
//      de sindicato casou com uma pessoa física. Errar de cliente aqui é mostrar
//      dívida de outra pessoa.)
//   3. Não achou pelo telefone — o que acontece na maioria das conversas — ela
//      busca por nome ou CPF.
//
// A ficha é organizada pelas perguntas que chegam no WhatsApp dela:
//   "o que tá vencido?"      -> filtro Vencidos (abre nele quando há atraso)
//   "quanto eu devo ao todo?"-> filtro Tudo em aberto + total do rodapé
//   "quando vence a próxima?"-> filtro A vencer + "próximo vencimento" no topo
//   "tenho crédito?"         -> aba Créditos
//   "já paguei isso"         -> aba Pagos, com período ajustável
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import backdropClose from '../helpers/backdropClose';
import ExtratoModal from './ExtratoModal';
import ReciboModal from './ReciboModal';

const dinheiro = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const dia = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

// dias_atraso > 0 = venceu e não pagou. Negativo = ainda vai vencer.
const rotuloAtraso = (dias) => {
  if (dias == null) return '—';
  if (dias > 0) return `${dias} ${dias === 1 ? 'dia' : 'dias'} em atraso`;
  if (dias === 0) return 'vence hoje';
  return `em ${Math.abs(dias)} ${Math.abs(dias) === 1 ? 'dia' : 'dias'}`;
};

const estaVencido = (t) => Number(t.dias_atraso) > 0;

const VERMELHO = '#dc3545';
const VERDE = '#3D8B37';

// ---------------------------------------------------------------- estilos
const rotulo = {
  fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em',
  textTransform: 'uppercase', color: 'var(--portal-muted)',
};

const secaoTitulo = { ...rotulo, marginBottom: 8 };

const th = {
  textAlign: 'left', padding: '8px 10px', fontSize: '0.7rem', fontWeight: 700,
  color: 'var(--portal-muted)', textTransform: 'uppercase', letterSpacing: '0.04em',
  whiteSpace: 'nowrap', position: 'sticky', top: 0, background: 'var(--portal-bg)',
  borderBottom: '1px solid var(--portal-border)',
};
const td = { padding: '7px 10px', fontSize: '0.85rem', whiteSpace: 'nowrap' };
const tdNum = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

const caixa = {
  border: '1px solid var(--portal-border)', borderRadius: 10, overflow: 'hidden',
};

// ------------------------------------------------------------- componentes

// Grade do resumo: cada número no seu quadro, separados por fio de 1px.
// Sem cartão-herói e sem número gigante — só hierarquia (rótulo pequeno em
// cima, valor forte embaixo).
function GradeResumo({ itens }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: 1, background: 'var(--portal-border)', ...caixa,
    }}>
      {itens.map((i) => (
        <div key={i.rotulo} style={{ background: 'var(--portal-surface)', padding: '9px 12px' }}>
          <div style={rotulo}>{i.rotulo}</div>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: i.cor, marginTop: 2 }}>{i.valor}</div>
          {i.sub && <div style={{ fontSize: '0.74rem', color: 'var(--portal-muted)' }}>{i.sub}</div>}
        </div>
      ))}
    </div>
  );
}

// modo: 'divida' (vencimento + atraso) | 'pago' (data do pagamento) |
//       'credito' (emissão; crédito não vence nem atrasa)
function TabelaTitulos({ titulos, modo = 'divida', total, rodape }) {
  if (!titulos.length) {
    return (
      <div style={{
        ...caixa, padding: '16px 12px', textAlign: 'center',
        fontSize: '0.85rem', color: 'var(--portal-muted)',
      }}>
        Nada aqui.
      </div>
    );
  }
  const rotuloData = modo === 'pago' ? 'Pagamento' : modo === 'credito' ? 'Emissão' : 'Vencimento';
  const rotuloValor = modo === 'pago' ? 'Valor' : modo === 'credito' ? 'Disponível' : 'Saldo';
  const dataDe = (t) => (modo === 'pago' ? t.pagamento : modo === 'credito' ? t.emissao : t.vencimento);
  const valorDe = (t) => (modo === 'pago' ? t.valor : t.saldo);

  return (
    <div style={caixa}>
      <div style={{ maxHeight: 320, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Documento</th>
              <th style={th}>Tipo</th>
              <th style={th}>{rotuloData}</th>
              <th style={{ ...th, textAlign: 'right' }}>{rotuloValor}</th>
              <th style={th}>Filial</th>
              {modo === 'divida' && <th style={th}>Situação</th>}
            </tr>
          </thead>
          <tbody>
            {titulos.map((t, i) => (
              <tr key={t.oid} style={{ background: i % 2 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                <td style={td}>{t.codigo || '—'}</td>
                <td style={{ ...td, color: 'var(--portal-muted)' }}>{t.tipo || '—'}</td>
                <td style={td}>{dia(dataDe(t))}</td>
                <td style={{ ...tdNum, color: modo === 'credito' ? VERDE : undefined }}>
                  {dinheiro(valorDe(t))}
                </td>
                <td style={{ ...td, color: 'var(--portal-muted)' }}>{t.filial || '—'}</td>
                {modo === 'divida' && (
                  <td style={{ ...td, color: estaVencido(t) ? VERMELHO : 'var(--portal-muted)' }}>
                    {rotuloAtraso(t.dias_atraso)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Total colado na tabela — é o número que ela fala pro cliente. */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
        padding: '9px 12px', borderTop: '1px solid var(--portal-border)',
        background: 'var(--portal-bg)', fontSize: '0.85rem', flexWrap: 'wrap',
      }}>
        <span style={{ color: 'var(--portal-muted)' }}>{rodape}</span>
        <strong style={{ fontVariantNumeric: 'tabular-nums', color: modo === 'credito' ? VERDE : undefined }}>
          {dinheiro(total)}
        </strong>
      </div>
    </div>
  );
}

function ListaCandidatos({ clientes, onEscolher }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {clientes.map((c) => (
        <button
          key={c.oid}
          type="button"
          onClick={() => onEscolher(c.oid)}
          style={{
            textAlign: 'left', background: 'var(--portal-bg)', color: 'var(--portal-text)',
            border: '1px solid var(--portal-border)', borderRadius: 8,
            padding: '9px 12px', cursor: 'pointer',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
          }}
        >
          <span style={{ minWidth: 0 }}>
            <span style={{ fontWeight: 600, fontSize: '0.9rem', display: 'block' }}>{c.nome}</span>
            <span style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
              {c.cpf_cnpj || 'sem CPF/CNPJ'}
            </span>
          </span>
          <i className="bi bi-chevron-right" style={{ color: 'var(--portal-muted)', flexShrink: 0 }} />
        </button>
      ))}
    </div>
  );
}

const PERIODOS_PAGOS = [3, 6, 12, 24];

export default function ConsultaClienteModal({
  aberto, numero, nomeContato, conversaId, onClose,
}) {
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [candidatos, setCandidatos] = useState([]);
  const [buscaFeita, setBuscaFeita] = useState(false);
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState(null); // null = ainda não buscou
  const [buscaAmpla, setBuscaAmpla] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [ficha, setFicha] = useState(null);
  const [aba, setAba] = useState('abertos');
  const [filtro, setFiltro] = useState('vencidos'); // vencidos | a_vencer | todos
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [mesesPagos, setMesesPagos] = useState(12);
  const [extratoAberto, setExtratoAberto] = useState(false);
  const [reciboAberto, setReciboAberto] = useState(false);

  const carregarFicha = useCallback(async (oid, meses = 12) => {
    setCarregando(true);
    setErro('');
    try {
      const r = await axios.get(`${API}/consulta-cliente/${oid}`, { params: { meses_pagos: meses } });
      const dados = r.data?.data || null;
      setFicha(dados);
      setMesesPagos(meses);
      setTipoFiltro('');
      // Abre no que ela mais precisa: se tem atraso, começa em Vencidos.
      setFiltro(dados?.resumo?.qtd_vencido > 0 ? 'vencidos' : 'todos');
    } catch (e) {
      setErro(e?.response?.data?.message || 'Não deu pra carregar a ficha do cliente.');
    } finally {
      setCarregando(false);
    }
  }, []);

  // Ao abrir: tenta o palpite pelo telefone da conversa.
  useEffect(() => {
    if (!aberto) return undefined;
    setFicha(null); setResultados(null); setTermo(''); setErro('');
    setCandidatos([]); setBuscaFeita(false); setBuscaAmpla(false); setAba('abertos');
    if (!numero) { setBuscaFeita(true); return undefined; }
    let vivo = true;
    setCarregando(true);
    axios.get(`${API}/consulta-cliente/sugestao`, { params: { numero } })
      .then((r) => {
        if (!vivo) return;
        // Um único candidato ainda assim NÃO abre a ficha direto: quem confirma
        // que é a pessoa certa é ela, olhando nome e CPF.
        setCandidatos(r.data?.data?.candidatos || []);
      })
      .catch(() => { if (vivo) setCandidatos([]); })
      .finally(() => { if (vivo) { setCarregando(false); setBuscaFeita(true); } });
    return () => { vivo = false; };
  }, [aberto, numero]);

  // ESC fecha — é o reflexo de quem usa o dia inteiro.
  useEffect(() => {
    if (!aberto) return undefined;
    // Com o extrato por cima, o ESC é dele: fechar os dois de uma vez faria a
    // crediarista perder a ficha que já tinha achado.
    const aoTeclar = (e) => { if (e.key === 'Escape' && !extratoAberto && !reciboAberto) onClose(); };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aberto, extratoAberto, reciboAberto, onClose]);

  const buscar = async (e) => {
    e?.preventDefault?.();
    const t = termo.trim();
    if (t.length < 3) { setErro('Digite ao menos 3 caracteres.'); return; }
    setBuscando(true); setErro('');
    try {
      const r = await axios.get(`${API}/consulta-cliente/buscar`, { params: { q: t } });
      setResultados(r.data?.data?.clientes || []);
      setBuscaAmpla(Boolean(r.data?.data?.ampla));
    } catch (err) {
      setErro(err?.response?.data?.message || 'Erro na busca.');
      setResultados([]);
    } finally {
      setBuscando(false);
    }
  };

  const tipos = useMemo(() => {
    if (!ficha) return [];
    return [...new Set(ficha.abertos.map((t) => t.tipo).filter(Boolean))].sort();
  }, [ficha]);

  const abertosFiltrados = useMemo(() => {
    if (!ficha) return [];
    return ficha.abertos.filter((t) => {
      if (tipoFiltro && t.tipo !== tipoFiltro) return false;
      if (filtro === 'vencidos') return estaVencido(t);
      if (filtro === 'a_vencer') return !estaVencido(t);
      return true;
    });
  }, [ficha, filtro, tipoFiltro]);

  const totalFiltrado = useMemo(
    () => abertosFiltrados.reduce((s, t) => s + Number(t.saldo || 0), 0),
    [abertosFiltrados],
  );

  // Primeiro título que ainda vai vencer (a lista vem do mais antigo pro mais novo).
  const proximoVencimento = useMemo(
    () => (ficha ? ficha.abertos.find((t) => !estaVencido(t) && t.vencimento) : null),
    [ficha],
  );

  if (!aberto) return null;

  const r = ficha?.resumo;
  const truncou = ficha && ficha.abertos.length < r.qtd_devido;

  const botaoFiltro = (ativo) => ({
    background: ativo ? VERDE : 'transparent',
    color: ativo ? '#fff' : 'var(--portal-text)',
    border: `1px solid ${ativo ? VERDE : 'var(--portal-border)'}`,
    borderRadius: 6, padding: '4px 10px', fontSize: '0.82rem',
    cursor: 'pointer', whiteSpace: 'nowrap',
  });

  const botaoAba = (ativo) => ({
    background: 'transparent', border: 'none', cursor: 'pointer',
    padding: '8px 2px', marginRight: 18, fontSize: '0.88rem',
    fontWeight: ativo ? 700 : 500,
    color: ativo ? 'var(--portal-text)' : 'var(--portal-muted)',
    borderBottom: `2px solid ${ativo ? VERDE : 'transparent'}`,
  });

  const nomeFiltro = filtro === 'vencidos' ? 'vencidos'
    : filtro === 'a_vencer' ? 'a vencer' : 'tudo em aberto';

  return (
    <>
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.72)' }} {...backdropClose(onClose)}>
      <div
        className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
        style={{ maxWidth: 900 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content" style={{ background: 'var(--portal-surface)', color: 'var(--portal-text)' }}>

          {/* ---------------- CABEÇALHO ---------------- */}
          <div className="modal-header" style={{ borderColor: 'var(--portal-border)', paddingBottom: 12 }}>
            <div style={{ minWidth: 0 }}>
              <h5 className="modal-title" style={{ fontSize: '1rem', marginBottom: 2 }}>
                <i className="bi bi-person-vcard me-2" />
                Consultar cliente
              </h5>
              <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                Microuniverso · somente consulta, nada é alterado no ERP
              </div>
            </div>
            {/* Trocar cliente e Fechar moram juntos: são as duas saídas da tela. */}
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {ficha && (
                <button
                  type="button"
                  onClick={() => { setFicha(null); setErro(''); }}
                  style={botaoFiltro(false)}
                >
                  <i className="bi bi-arrow-left me-1" /> Trocar cliente
                </button>
              )}
              <button type="button" onClick={onClose} title="Fechar (Esc)" style={botaoFiltro(false)}>
                <i className="bi bi-x-lg me-1" /> Fechar
              </button>
            </div>
          </div>

          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {erro && (
              <div style={{
                fontSize: '0.85rem', color: VERMELHO, border: `1px solid ${VERMELHO}`,
                borderRadius: 8, padding: '8px 10px',
              }}>
                {erro}
              </div>
            )}

            {/* ---------------- ESCOLHA DO CLIENTE ---------------- */}
            {!ficha && (
              <>
                <section>
                  <div style={secaoTitulo}>Pelo telefone da conversa</div>
                  {carregando && (
                    <div style={{ fontSize: '0.85rem', color: 'var(--portal-muted)' }}>Procurando…</div>
                  )}
                  {!carregando && candidatos.length > 0 && (
                    <>
                      <div style={{ fontSize: '0.82rem', color: 'var(--portal-muted)', marginBottom: 8 }}>
                        Cadastro com o telefone de {nomeContato || 'quem está na conversa'} —
                        {' '}confira o nome e o CPF antes de abrir.
                      </div>
                      <ListaCandidatos clientes={candidatos} onEscolher={(oid) => carregarFicha(oid)} />
                    </>
                  )}
                  {!carregando && buscaFeita && candidatos.length === 0 && (
                    <div style={{
                      ...caixa, padding: '12px', fontSize: '0.85rem', color: 'var(--portal-muted)',
                    }}>
                      Nenhum cadastro com esse telefone. Busque pelo nome ou CPF abaixo.
                    </div>
                  )}
                </section>

                <section style={{ borderTop: '1px solid var(--portal-border)', paddingTop: 16 }}>
                  <div style={secaoTitulo}>Buscar por nome ou CPF/CNPJ</div>
                  <form onSubmit={buscar} style={{ display: 'flex', gap: 6 }}>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Ex.: MARIA DA SILVA ou 058.532.856-02"
                      value={termo}
                      onChange={(e) => setTermo(e.target.value)}
                      style={{
                        background: 'var(--portal-bg)', color: 'var(--portal-text)',
                        borderColor: 'var(--portal-border)',
                      }}
                    />
                    <button
                      type="submit"
                      disabled={buscando}
                      style={{ ...botaoFiltro(true), padding: '5px 14px' }}
                    >
                      {buscando ? 'Buscando…' : 'Buscar'}
                    </button>
                  </form>

                  {resultados && resultados.length === 0 && !buscando && (
                    <div style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', marginTop: 10 }}>
                      Nenhum cliente encontrado com esse termo.
                    </div>
                  )}
                  {resultados && resultados.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      {buscaAmpla && (
                        <div style={{ fontSize: '0.8rem', color: 'var(--portal-muted)', marginBottom: 6 }}>
                          Nada começando com o que você digitou — estes têm o termo no meio do nome.
                        </div>
                      )}
                      <ListaCandidatos clientes={resultados} onEscolher={(oid) => carregarFicha(oid)} />
                    </div>
                  )}
                </section>
              </>
            )}

            {/* ---------------- FICHA ---------------- */}
            {ficha && (
              <>
                {/* Identificação */}
                <section style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>{ficha.cliente.nome}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--portal-muted)', marginTop: 2 }}>
                      {ficha.cliente.cpf_cnpj || 'sem CPF/CNPJ'}
                      {ficha.cliente.codigo ? ` · código ${ficha.cliente.codigo}` : ''}
                      {ficha.telefones.length > 0
                        && ` · ${ficha.telefones.map((t) => t.numero).join(' · ')}`}
                    </div>
                  </div>
                  {/* Fica na identificação, e não junto dos filtros, porque o
                      extrato é sempre do cliente inteiro — os filtros da tela
                      não mudam o que sai no PDF. */}
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => setExtratoAberto(true)}
                      title="Mesmo extrato que o Microuniverso imprime"
                      style={{ ...botaoFiltro(false), flexShrink: 0 }}
                    >
                      <i className="bi bi-file-earmark-text me-1" /> Extrato
                    </button>
                    {/* Comprovante de um pagamento JÁ FEITO — vizinho do extrato
                        porque é o outro documento que o cliente pede. */}
                    <button
                      type="button"
                      onClick={() => setReciboAberto(true)}
                      title="Recibo de um pagamento — o mesmo que o Microuniverso reimprime"
                      style={{ ...botaoFiltro(false), flexShrink: 0 }}
                    >
                      <i className="bi bi-receipt me-1" /> Recibo
                    </button>
                  </div>
                </section>

                {/* Situação financeira */}
                <section>
                  <div style={secaoTitulo}>Situação financeira</div>
                  <GradeResumo
                    itens={[
                      {
                        rotulo: 'Devendo',
                        valor: dinheiro(r.total_devido),
                        sub: `${r.qtd_devido} ${r.qtd_devido === 1 ? 'título' : 'títulos'}`,
                      },
                      {
                        rotulo: 'Vencido',
                        valor: dinheiro(r.total_vencido),
                        cor: r.total_vencido > 0 ? VERMELHO : undefined,
                        sub: r.maior_atraso != null
                          ? `${r.qtd_vencido} · maior atraso ${r.maior_atraso} dias`
                          : `${r.qtd_vencido} títulos`,
                      },
                      // Limite cadastrado no ERP. Só aparece se o cliente tiver
                      // ficha de crédito — muita gente não tem.
                      ...(ficha.limite_credito ? [{
                        rotulo: 'Limite de crédito',
                        valor: dinheiro(ficha.limite_credito.limite),
                        sub: ficha.limite_credito.data
                          ? `definido em ${dia(ficha.limite_credito.data)}`
                          : null,
                      }] : []),
                      ...(r.total_credito > 0 ? [{
                        rotulo: 'Crédito',
                        valor: dinheiro(r.total_credito),
                        cor: VERDE,
                        sub: `${r.qtd_credito} ${r.qtd_credito === 1 ? 'lançamento' : 'lançamentos'}`,
                      }, {
                        rotulo: 'Saldo líquido',
                        valor: dinheiro(Math.abs(r.saldo_liquido)),
                        cor: r.saldo_liquido < 0 ? VERDE : undefined,
                        sub: r.saldo_liquido < 0 ? 'a favor do cliente' : 'depois de usar o crédito',
                      }] : []),
                    ]}
                  />
                  <div style={{
                    display: 'flex', gap: '2px 16px', flexWrap: 'wrap',
                    marginTop: 8, fontSize: '0.78rem', color: 'var(--portal-muted)',
                  }}>
                    {proximoVencimento && (
                      <span>Próximo vencimento: <strong>{dia(proximoVencimento.vencimento)}</strong></span>
                    )}
                    <span>Última compra: <strong>{dia(r.ultima_compra)}</strong></span>
                    <span>Último pagamento: <strong>{dia(r.ultimo_pagamento)}</strong></span>
                    {/* Mesma conta da tela "Documentos Quitados" do ERP — fecha
                        ao centavo com ela, então dá pra conferir lado a lado. */}
                    <span>
                      Já pagou (total): <strong>{dinheiro(r.total_pago)}</strong>
                      {r.qtd_pago > 0 && ` em ${r.qtd_pago} ${r.qtd_pago === 1 ? 'título' : 'títulos'}`}
                    </span>
                    {/* Como ele paga — responde "posso vender fiado de novo?" */}
                    {ficha.comportamento && (
                      <span>
                        Costuma pagar:{' '}
                        <strong>
                          {ficha.comportamento.qtd_atrasos
                            ? `atrasou ${ficha.comportamento.qtd_atrasos}×`
                              + ` · média ${ficha.comportamento.media_atraso} dias`
                              + ` · pior ${ficha.comportamento.maior_atraso}`
                            : 'nunca atrasou'}
                        </strong>
                      </span>
                    )}
                  </div>

                  {ficha.limite_credito?.observacao && (
                    <div style={{
                      marginTop: 8, fontSize: '0.8rem', ...caixa, padding: '8px 10px',
                    }}>
                      <span style={rotulo}>Observação do crediário</span>
                      <div style={{ marginTop: 3 }}>{ficha.limite_credito.observacao}</div>
                    </div>
                  )}
                </section>

                {/* Abas */}
                <section>
                  <div style={{ borderBottom: '1px solid var(--portal-border)', marginBottom: 12 }}>
                    <button type="button" style={botaoAba(aba === 'abertos')} onClick={() => setAba('abertos')}>
                      Em aberto <span style={{ color: 'var(--portal-muted)' }}>({ficha.abertos.length})</span>
                    </button>
                    {ficha.creditos.length > 0 && (
                      <button type="button" style={botaoAba(aba === 'creditos')} onClick={() => setAba('creditos')}>
                        Créditos <span style={{ color: 'var(--portal-muted)' }}>({ficha.creditos.length})</span>
                      </button>
                    )}
                    <button type="button" style={botaoAba(aba === 'pagos')} onClick={() => setAba('pagos')}>
                      Pagos <span style={{ color: 'var(--portal-muted)' }}>({ficha.pagos.length})</span>
                    </button>
                  </div>

                  {aba === 'abertos' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {/* Barra de filtros, visualmente separada da tabela */}
                      <div style={{
                        display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
                        background: 'var(--portal-bg)', border: '1px solid var(--portal-border)',
                        borderRadius: 8, padding: '8px 10px',
                      }}>
                        <span style={rotulo}>Mostrar</span>
                        <button type="button" style={botaoFiltro(filtro === 'vencidos')} onClick={() => setFiltro('vencidos')}>
                          Vencidos
                        </button>
                        <button type="button" style={botaoFiltro(filtro === 'a_vencer')} onClick={() => setFiltro('a_vencer')}>
                          A vencer
                        </button>
                        <button type="button" style={botaoFiltro(filtro === 'todos')} onClick={() => setFiltro('todos')}>
                          Tudo em aberto
                        </button>
                        {tipos.length > 1 && (
                          <select
                            className="form-select form-select-sm"
                            value={tipoFiltro}
                            onChange={(e) => setTipoFiltro(e.target.value)}
                            style={{
                              width: 'auto', marginLeft: 'auto', background: 'var(--portal-surface)',
                              color: 'var(--portal-text)', borderColor: 'var(--portal-border)',
                              fontSize: '0.82rem',
                            }}
                          >
                            <option value="">Todos os tipos</option>
                            {tipos.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        )}
                      </div>

                      <TabelaTitulos
                        titulos={abertosFiltrados}
                        modo="divida"
                        total={totalFiltrado}
                        rodape={`${abertosFiltrados.length} ${abertosFiltrados.length === 1 ? 'título' : 'títulos'}`
                          + ` · ${nomeFiltro}${tipoFiltro ? ` · ${tipoFiltro}` : ''}`}
                      />

                      {truncou && (
                        <div style={{
                          fontSize: '0.78rem', color: '#F5A623',
                          border: '1px solid #F5A623', borderRadius: 8, padding: '8px 10px',
                        }}>
                          Este cliente tem {r.qtd_devido} títulos em aberto e a lista mostra os{' '}
                          {ficha.abertos.length} mais antigos — o total do rodapé considera só o que está
                          listado. Os números de "Situação financeira" consideram todos.
                        </div>
                      )}
                    </div>
                  )}

                  {aba === 'creditos' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ fontSize: '0.82rem', color: 'var(--portal-muted)' }}>
                        Crédito que o cliente tem com a loja (devolução, cancelamento ou indicação).
                        Pode abater em compra ou em dívida.
                      </div>
                      <TabelaTitulos
                        titulos={ficha.creditos}
                        modo="credito"
                        total={ficha.creditos.reduce((s, t) => s + Number(t.saldo || 0), 0)}
                        rodape={`${ficha.creditos.length} ${ficha.creditos.length === 1 ? 'crédito' : 'créditos'} disponíveis`}
                      />
                    </div>
                  )}

                  {aba === 'pagos' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{
                        display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
                        background: 'var(--portal-bg)', border: '1px solid var(--portal-border)',
                        borderRadius: 8, padding: '8px 10px',
                      }}>
                        <span style={rotulo}>Período</span>
                        {PERIODOS_PAGOS.map((m) => (
                          <button
                            key={m}
                            type="button"
                            style={botaoFiltro(mesesPagos === m)}
                            disabled={carregando}
                            onClick={() => carregarFicha(ficha.cliente.oid, m)}
                          >
                            {m} meses
                          </button>
                        ))}
                      </div>
                      <TabelaTitulos
                        titulos={ficha.pagos}
                        modo="pago"
                        total={ficha.pagos.reduce((s, t) => s + Number(t.valor || 0), 0)}
                        rodape={`${ficha.pagos.length} ${ficha.pagos.length === 1 ? 'pagamento' : 'pagamentos'}`
                          + ` · últimos ${ficha.meses_pagos} meses`}
                      />
                      {ficha.pagos_truncados && (
                        <div style={{
                          fontSize: '0.78rem', color: '#F5A623',
                          border: '1px solid #F5A623', borderRadius: 8, padding: '8px 10px',
                        }}>
                          Cliente com muito histórico: a lista mostra os {ficha.pagos.length} pagamentos
                          mais recentes do período e o total do rodapé soma só esses. O
                          «Já pagou (total)» lá em cima considera os {r.qtd_pago} títulos de toda a vida.
                        </div>
                      )}
                    </div>
                  )}
                </section>
              </>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Fora do overlay de cima de propósito: aninhado, o clique nele borbulharia
        pro `onClick={onClose}` do modal da consulta e fecharia os dois. */}
    <ReciboModal
      aberto={reciboAberto && !!ficha}
      oid={ficha?.cliente?.oid}
      cliente={ficha?.cliente}
      conversaId={conversaId}
      onClose={() => setReciboAberto(false)}
    />
    <ExtratoModal
      aberto={extratoAberto && !!ficha}
      oid={ficha?.cliente?.oid}
      cliente={ficha?.cliente}
      conversaId={conversaId}
      onClose={() => setExtratoAberto(false)}
    />
    </>
  );
}
